import { useEffect, useRef, useState } from 'react';
import { isNullish } from '@sas/nova-core/typeguards';
import { transparentize } from 'polished';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useMounted } from '../../hooks/useMounted';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { renderDesignTokens, useDesignTokens } from '../../theme';
import { setRef } from '../../util/ref-util';
import { postfixTestId } from '../../util/testId';
import { useDensity } from '../Density';
import { DropdownDesign } from '../Dropdown/__internal__/DropdownDesign';
import { isOptionPopperSeparator } from '../Dropdown/__internal__/utils/typeguards';
import { GlobalSettingsKeys } from '../Settings/GlobalSettingsKeys';
import { useGetGlobalSetting } from '../Settings/GlobalSettingsProvider';
import { BaseInputBox } from './__internal__/BaseInputBox';
import bundle from './NovaComponents-ComboBox-gui-icu.properties';
import './ComboBox.css';
import { jsx as _jsx } from "react/jsx-runtime";
const useTextLocalizer = createUseTextLocalizer(bundle);
/**
 * The ComboBox component is a Select control with the added functionality of allowing the end-user
 * to specify their own custom value.
 *
 * @component
 * @param {ComboBoxProps} props the ComboBoxProps properties
 * @returns the ComboBox element
 * @example
 * ```typescript
 * const [selectedIndex, setSelectedIndex] = useState<number | null | undefined>(0);
 * const [value, setValue] = useState<string>('');
 * return (
 *   <ComboBox
 *    value={value}
 *    setValue={setValue}
 *    selectedIndex={selectedIndex}
 *    onSelected={(selectedIndex) => {
 *      setSelectedIndex(selectedIndex);
 *    }}
 *  />
 * );
 * ```
 */

export const ComboBox = ({
  futureTheme,
  comboBoxTokens,
  ...props
}) => {
  const localize = useTextLocalizer();
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(props.selectedIndex);
  const [committedValue, setCommittedValue] = useState({
    value: props.value,
    selectedIndex: props.selectedIndex
  });
  const refInputBox = useRef(null);
  const showSuggestions = props.showSuggestions ?? true;
  const refInput = useRef(null);
  const mountedRef = useMounted();

  // Feature flag for 'New Token Values'
  const isNewTokenValueFlagEnabled = useFeatureFlag('NOVACOMMONS-22288_ThemeModernization_NewTokenValues');
  const dt = useDesignTokens();
  const {
    density
  } = useDensity();
  const isCustomFocusTurnedOn = useGetGlobalSetting(GlobalSettingsKeys.FOCUS_OVERRIDE) === 'true';
  const tokens = {
    ...getComboBoxDesignTokens(dt, {
      isCustomFocusTurnedOn,
      isNewTokenValueFlagEnabled,
      disabled: props.disabled ?? false,
      density,
      overrides: comboBoxTokens
    }),
    ...futureTheme?.(dt)
  };
  const items = props.values;
  const dropdownItems = [];
  if (items) {
    for (const item of items) {
      // need to remove separator from the items
      if (isOptionPopperSeparator(item)) {
        continue;
      } else {
        dropdownItems.push(item);
      }
    }
  }
  const handleKeyDown = e => {
    if (!mountedRef.current) return;
    if (props.readOnly) return;
    if (e.key === 'Escape') {
      // reminder that higher level DropdownDesign checks for the open
      // status and does some esc handling as well
      let eventHandled = false;
      if (committedValue.selectedIndex !== props.selectedIndex || committedValue.value !== props.value) {
        props.onSelected?.(committedValue.selectedIndex);
        props.setValue(committedValue.value ?? '');
        if (props.values && !isNullish(committedValue.selectedIndex) && committedValue.selectedIndex >= 0) {
          const elem = props.values.find((item, elIndex) => {
            if (elIndex === committedValue.selectedIndex) return item;
          });
          const text = elem.text;
          refInputBox.current?.onChange(text ?? '', true, null, true);
        } else {
          refInputBox.current?.onChange(committedValue.value ?? '', true, null, true);
        }
        eventHandled = true;
      } else {
        const cancelHandled = props.onCancel?.();
        if (props.onCancel && cancelHandled !== false) eventHandled = true;
      }
      if (eventHandled) {
        e.preventDefault();
        e.stopPropagation();
      }
      // set a timeout to ensure it executes last in the chain
      // This setTimeout is used just to select the text in the input field. It
      // does not do any state updates or event callbacks, nor should it ever be.
      setTimeout(() => {
        refInput.current?.setSelectionRange(refInput.current?.value.length, refInput.current?.value.length, undefined);
      }, 1);
      setIsOpen(false);
    }
    if (e.key === 'Tab') {
      setIsOpen(false);
    }
    if (refInputBox.current?.isSuggestionsOpen() && ['ArrowUp', 'ArrowDown'].includes(e.key)) {
      e.stopPropagation();
    }
  };
  const handleSelectedIndex = index => {
    if (!mountedRef.current) return;
    if (refInputBox.current?.isSuggestionsOpen() || !refInputBox.current?.isSuggestionsOpen() && index === -1) {
      props.onSelected?.(index);
      const elem = dropdownItems.find((item, elIndex) => {
        if (elIndex === index) return item;
      });
      const text = elem?.text;
      if (!isNullish(text)) {
        setCommittedValue({
          value: text,
          selectedIndex: index
        });
      }
    }
  };
  const beforeOpen = () => {
    // If user entered a value that is in the list, ensure the correct index is selected when opening dropdown
    let index = -1;
    if (props.selectedIndex === -1) {
      index = dropdownItems?.findIndex(obj => obj.text === refInput.current?.value) ?? -1;
      if (index > -1) {
        setFocusedIndex(index);
        props.onSelected?.(index);
      }
    }
    return Promise.resolve(false);
  };
  const dropdownItemsRef = useRef(dropdownItems);
  dropdownItemsRef.current = dropdownItems;
  useEffect(() => {
    !refInputBox.current?.isSuggestionsOpen() && setFocusedIndex(props.selectedIndex);
    const elem = dropdownItemsRef.current.find((item, elIndex) => {
      if (elIndex === props.selectedIndex) return item;
    });
    const text = elem?.text;
    if (!isNullish(text) && text !== refInput.current?.value) {
      setCommittedValue({
        value: text,
        selectedIndex: props.selectedIndex
      });
      refInputBox.current?.onChange(text, true, props.selectedIndex);
    }
  }, [props.selectedIndex]);
  useEffect(() => {
    // If there is a custom value and the user starts scrolling thru the dropdown, need to update
    // the value in the input to the selection from the dropdown
    if (props.selectedIndex === -1 && isOpen) {
      props.onSelected?.(focusedIndex);
    }
  }, [focusedIndex, isOpen, props]);
  return /*#__PURE__*/_jsx(DropdownDesign, {
    ...props,
    style: {
      ...tokens,
      ...props.style
    },
    testId: postfixTestId(props.testId, 'dropdown'),
    required: undefined,
    status: undefined,
    message: undefined,
    hasInput: true,
    isOpen: isOpen,
    setIsOpen: setIsOpen,
    focusedIndex: focusedIndex,
    setFocusedIndex: setFocusedIndex,
    value: props.value,
    readOnly: props.readOnly,
    onBeforeOpen: beforeOpen,
    showSelectedPseudoFocus: true,
    onSelected: index => {
      setCommittedValue({
        value: props.value,
        selectedIndex: index
      });
      if (index !== -1) {
        refInputBox.current?.setIsValid(true);
        if (isOpen) {
          setTimeout(() => {
            refInputBox.current?.focus();
            refInputBox.current?.closeSuggestions();
          }, 0);
        }
        if (isOpen || !refInputBox.current?.isSuggestionsOpen()) {
          setTimeout(() => {
            if (dropdownItems) {
              const elem = dropdownItems.find((item, elIndex) => {
                if (elIndex === index) return item;
              });
              const text = elem.text;
              refInputBox.current?.onChange(text ?? '', true, index);
              setTimeout(() => {
                if (props.type !== 'email') {
                  refInput.current?.setSelectionRange(0, refInput.current?.value.length, undefined);
                }
              }, 0);
            }
          }, 0);
        }

        // Don't need to set focus index when the suggestions window is open
        !refInputBox.current?.isSuggestionsOpen() && setFocusedIndex(index);
      }
      props.onSelected?.(index);
    },
    ariaLabel: localize('options.aria'),
    ...(props.enableVirtualScrolling && items && {
      virtualItems: {
        count: items.length ?? 0,
        renderItem: ({
          index
        }) => {
          return {
            text: items[index].text,
            secondaryText: items[index].secondaryText,
            concatSecondaryText: items[index].concatSecondaryText,
            icon: items[index].icon,
            iconAriaLabel: items[index].iconAriaLabel,
            iconTooltip: items[index].iconTooltip,
            id: items[index].id,
            indented: items[index].indented,
            tooltip: items[index].tooltip,
            showSeparator: items[index].showSeparator
          };
        },
        width: props.inputContainerWidth ?? refInput.current?.parentElement?.parentElement?.getBoundingClientRect().width
      }
    }),
    renderSelectBox: renderProps => /*#__PURE__*/_jsx(BaseInputBox, {
      ...renderProps,
      ref: refInputBox,
      inputRef: element => {
        refInput.current = element;
        setRef(props.inputRef, element);
      },
      id: props.id,
      testId: postfixTestId(props.testId, 'inputBox'),
      value: props.value,
      items: showSuggestions ? props.values : undefined,
      dropdownItems: props.values,
      filterType: props.filterType,
      setValue: props.setValue,
      editable: props.editable,
      type: props.type,
      allowIntegersOnly: props.allowIntegersOnly,
      required: props.required,
      message: props.message,
      status: props.status,
      placeholder: props.placeholder,
      ariaActiveDescendant: props.ariaActiveDescendant,
      ariaDescribedBy: props.ariaDescribedBy,
      ariaLabel: props.ariaLabel,
      ariaLabelledBy: props.ariaLabelledBy,
      onKeyDown: handleKeyDown,
      onKeyUp: () => {
        // Whenever the suggestions list is open, close the drowndown
        refInputBox.current?.isSuggestionsOpen() && setIsOpen(false);
      },
      readOnly: props.readOnly,
      enableAutoSelection: props.enableAutoSelection,
      onCommit: event => {
        const index = props.values?.findIndex(obj => obj.text === refInput.current?.value) ?? -1;
        setCommittedValue({
          value: event.value,
          selectedIndex: index
        });
        props.onCommit?.(event);
      },
      onCancel: props.onCancel,
      onFocus: props.onFocus,
      onBlur: props.onBlur,
      onChange: props.onChange,
      onSelected: props.onSelected,
      setSelectedIndex: handleSelectedIndex,
      maxLength: props.maxLength,
      commitOnEnterInDialog: props.commitOnEnterInDialog,
      validateFunction: props.validateFunction
    })
  });
};
export const getComboBoxDesignTokens = (dt, opts) => {
  // Outline Focus Indicator
  let outline;
  let outlineOffset;
  if (dt.themeVariant !== 'high-contrast' && !opts.isCustomFocusTurnedOn && !opts.disabled) {
    outline = 'none';
  } else {
    outline = 'var(--focus-light-bg)';
    outlineOffset = 'calc(-1 * var(--focus-width) - 2px)';
  }
  const placeholderTokens = opts.overrides?.theme?.({
    state: 'placeholder'
  });
  const tokenOverrides = {
    ...opts.overrides?.font,
    ...opts.overrides?.theme?.({
      state: 'normal'
    }),
    ...(() => {
      const t = {};
      Object.entries(opts.overrides?.theme?.({
        state: 'hover'
      }) ?? {}).forEach(([k, v]) => {
        t[`hover-${k}`] = v;
      });
      return t;
    })(),
    ...(() => {
      const t = {};
      Object.entries(opts.overrides?.theme?.({
        state: 'active'
      }) ?? {}).forEach(([k, v]) => {
        t[`active-${k}`] = v;
      });
      return t;
    })(),
    ...(placeholderTokens ? {
      'placeholder-fg': placeholderTokens.fg
    } : undefined)
  };
  const densityTokens = opts.density === 'high' ? {
    'font-size': dt.tokens['font-size-body-text-s'],
    'min-block-size': '1.5rem'
  } : {
    'font-size': dt.tokens['font-size-body-text-m'],
    'min-block-size': '1.75rem'
  };
  return {
    ...renderDesignTokens({
      bd: dt.tokens['color-action-border-bottom-control-normal'],
      bg: dt.tokens['color-action-background-control-normal'],
      fg: dt.tokens['color-action-text-control-normal'],
      'hover-bd': dt.tokens['color-action-border-bottom-control-hover'],
      'hover-bg': dt.tokens['color-action-background-control-hover'],
      'hover-fg': dt.tokens['color-action-text-control-hover'],
      'active-bd': dt.tokens['color-action-border-bottom-control-selected'],
      'active-bg': dt.tokens['color-action-background-control-selected'],
      'active-fg': dt.tokens['color-action-text-control-selected'],
      'disabled-fg': dt.tokens['color-action-text-control-disabled'],
      'disabled-bd': dt.tokens['color-action-border-control-disabled'],
      'disabled-bg': dt.tokens['color-action-background-control-disabled'],
      'readonly-bd': 'transparent',
      'readonly-bg': dt.tokens['color-action-background-control-normal'],
      'readonly-fg': dt.tokens['color-action-text-control-normal'],
      'placeholder-fg': dt.tokens['color-action-text-control-placeholder'],
      'spacing-xs': dt.tokens['space-base-xs'],
      'spacing-s': dt.tokens['space-base-s'],
      'focus-outline': 'var(--focus-light-bg)',
      'focus-outline-offset': 'calc(-1 * var(--focus-width))',
      ...(!opts?.isNewTokenValueFlagEnabled && {
        'focus-outline': outline,
        'focus-outline-offset': outlineOffset
      }),
      'error-bd': dt.tokens['color-action-border-bottom-control-alert'],
      'error-bg': dt.tokens['color-action-background-control-alert'],
      'warning-bd': dt.tokens['color-action-border-bottom-control-warning'],
      'warning-bg': dt.tokens['color-action-background-control-warning'],
      ...densityTokens,
      ...tokenOverrides,
      'bd-width': '0 0 1px 0',
      ...(!opts?.isNewTokenValueFlagEnabled && {
        'bd-width': dt.tokens['size-border-width-s']
      }),
      'bottom-bd-anim-dur': dt.tokens['duration-300'],
      'bottom-bd-anim-tm-ease-in': dt.tokens['ease-in'],
      'bottom-bd-anim-tm-ease-out': dt.tokens['ease-out']
    }, 'combobox'),
    ...(!dt.enableNovaTokens && getComboBoxLegacyDesignTokens(dt, opts))
  };
};
export const getComboBoxLegacyDesignTokens = (dt, opts) => {
  const focus = dt.reportTokens.stateFocus.onLightBg;
  const placeholderTokens = opts.overrides?.theme?.({
    state: 'placeholder'
  });
  const tokenOverrides = {
    ...opts.overrides?.font,
    ...opts.overrides?.theme?.({
      state: 'normal'
    }),
    ...(() => {
      const t = {};
      Object.entries(opts.overrides?.theme?.({
        state: 'hover'
      }) ?? {}).forEach(([k, v]) => {
        t[`hover-${k}`] = v;
      });
      return t;
    })(),
    ...(() => {
      const t = {};
      Object.entries(opts.overrides?.theme?.({
        state: 'active'
      }) ?? {}).forEach(([k, v]) => {
        t[`active-${k}`] = v;
      });
      return t;
    })(),
    ...(placeholderTokens ? {
      'placeholder-fg': placeholderTokens.fg
    } : undefined)
  };
  return {
    ...renderDesignTokens({
      bd: dt.reportTokens.stateStandard.normal.bd,
      bg: dt.reportTokens.stateStandard.normal.bg,
      fg: dt.reportTokens.stateStandard.normal.fg,
      'hover-bd': dt.reportTokens.stateStandard.hover.bd,
      'hover-bg': dt.reportTokens.stateStandard.hover.bg,
      'hover-fg': dt.reportTokens.stateStandard.hover.fg,
      'active-bd': dt.reportTokens.stateStandard.active.bd,
      'active-bg': dt.reportTokens.stateStandard.active.bg,
      'active-fg': dt.reportTokens.stateStandard.active.fg,
      'disabled-bd': dt.reportTokens.stateStandard.disabled.bd,
      'disabled-bg': dt.reportTokens.stateStandard.disabled.bg,
      'disabled-fg': dt.reportTokens.stateStandard.disabled.fg,
      'readonly-bd': 'transparent',
      'readonly-bg': dt.reportTokens.stateStandard.normal.bg,
      'readonly-fg': dt.reportTokens.stateStandard.normal.fg,
      'placeholder-fg': dt.themeVariant === 'dark' ? transparentize(0.35, dt.c.white) : dt.c.n1,
      'focus-outline': `${focus.bd} ${focus.bdStyle} ${focus.bdWidth}`,
      'focus-outline-offset': `calc(-2px + calc(-1 * ${focus.bdWidth}))`,
      'error-bd': dt.reportTokens.stateStatus.alert.bd,
      'warning-bd': dt.reportTokens.stateStatus.warning.bd,
      'font-size': dt.reportTokens.font.size.m,
      ...tokenOverrides
    }, 'combobox')
  };
};
//# sourceMappingURL=ComboBox.js.map